import { type RefObject, useCallback, useEffect, useRef } from 'react';

import { RECAPTCHA_SITE_KEY } from '../constants';
import '../types';

type UseRecaptchaWidgetOptions = {
  containerRef: RefObject<HTMLDivElement>;
  onChange: (token: string | null) => void;
  onRendered?: () => void;
};

export const useRecaptchaWidget = ({
  containerRef,
  onChange,
  onRendered,
}: UseRecaptchaWidgetOptions) => {
  const widgetIdRef = useRef<number | null>(null);
  const onChangeRef = useRef(onChange);
  const onRenderedRef = useRef(onRendered);
  const scheduleRenderRef = useRef<(() => void) | undefined>();

  const onRecaptchaLoaded = useRef(() => scheduleRenderRef.current?.()).current;

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onRenderedRef.current = onRendered;
  }, [onRendered]);

  const reset = useCallback(() => {
    if (widgetIdRef.current !== null && window.grecaptcha) {
      window.grecaptcha.reset(widgetIdRef.current);
    }
    onChangeRef.current(null);
  }, []);

  const getResponse = useCallback(() => {
    if (widgetIdRef.current === null || !window.grecaptcha?.getResponse)
      return null;

    const token = window.grecaptcha.getResponse(widgetIdRef.current);
    return token || null;
  }, []);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) {
      onChangeRef.current(null);
      return undefined;
    }

    let cancelled = false;
    let listenerAttached = false;

    const detachRecaptchaListener = () => {
      if (!listenerAttached) return;

      window.removeEventListener('recaptcha-loaded', onRecaptchaLoaded);
      listenerAttached = false;
    };

    const attachRecaptchaListener = () => {
      if (listenerAttached) return;

      window.addEventListener('recaptcha-loaded', onRecaptchaLoaded);
      listenerAttached = true;
    };

    const renderWidget = () => {
      if (
        cancelled ||
        !containerRef.current ||
        widgetIdRef.current !== null ||
        !window.grecaptcha?.render
      ) {
        return;
      }

      widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
        sitekey: RECAPTCHA_SITE_KEY,
        callback: (token) => onChangeRef.current(token),
        'expired-callback': () => onChangeRef.current(null),
        'error-callback': () => onChangeRef.current(null),
      });

      requestAnimationFrame(() => onRenderedRef.current?.());
    };

    scheduleRenderRef.current = () => {
      if (cancelled) return;

      if (window.grecaptcha?.ready) {
        detachRecaptchaListener();
        window.grecaptcha.ready(renderWidget);
        return;
      }

      if (window.grecaptcha?.render) {
        detachRecaptchaListener();
        renderWidget();
        return;
      }

      attachRecaptchaListener();
    };

    scheduleRenderRef.current();

    return () => {
      cancelled = true;
      detachRecaptchaListener();
      scheduleRenderRef.current = undefined;

      if (widgetIdRef.current !== null && window.grecaptcha?.reset) {
        window.grecaptcha.reset(widgetIdRef.current);
      }

      widgetIdRef.current = null;
    };
  }, [containerRef, onRecaptchaLoaded]);

  return { reset, getResponse };
};
